import React, { useState } from "react";
import { Phone, Mail, Send, ArrowUpRight } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import useReveal from "../../hooks/useReveal";

const TELEGRAM_USERNAME = "nmn_energy";
const PHONE = "+905488267252";
const EMAIL = "gufenerb@gmail.com";

const Contact = () => {
  const { t, lang } = useLanguage();
  const ref = useReveal();

  const [form, setForm] = useState({ name: "", contact: "", message: "" });

  const buildTelegramUrl = () => {
    const header =
      lang === "ru"
        ? "Заявка с сайта NMN Energy Consulting"
        : "New inquiry from NMN Energy Consulting";
    const msg = [
      header,
      "",
      `Name: ${form.name || "-"}`,
      `Contact: ${form.contact || "-"}`,
      `Brief: ${form.message || "-"}`,
    ].join("\n");
    const encoded = encodeURIComponent(msg);
    // Deep link that opens Telegram with prefilled text to the handle
    return `https://t.me/${TELEGRAM_USERNAME}?text=${encoded}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    window.open(buildTelegramUrl(), "_blank", "noopener,noreferrer");
  };

  return (
    <section
      id="contact"
      ref={ref}
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-black overflow-hidden"
    >
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(232,212,168,0.06)_0%,transparent_60%)] pointer-events-none"></div>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left column */}
          <div className="lg:col-span-5">
            <p
              className="reveal font-mono-l text-[11px] text-[#e8d4a8] mb-5 flex items-center gap-3"
              data-testid="contact-eyebrow"
            >
              <span className="w-6 h-px bg-[#e8d4a8]"></span>
              {t.contact.eyebrow}
            </p>
            <h2
              className="reveal reveal-delay-1 font-display text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-6"
              data-testid="contact-title"
            >
              {t.contact.title}
            </h2>
            <p className="reveal reveal-delay-2 text-white/60 text-base leading-relaxed font-light mb-8">
              {t.contact.subtitle}
            </p>

            {/* Prefilled Telegram quick-button */}
            <a
              href={`https://t.me/${TELEGRAM_USERNAME}?text=${encodeURIComponent(
                t.contact.prefilledMessage
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="reveal reveal-delay-2 group inline-flex items-center gap-3 px-6 py-4 mb-12 bg-[#e8d4a8] text-black text-[12px] tracking-[0.2em] uppercase font-medium hover:bg-white transition-colors duration-300"
              data-testid="contact-quick-telegram-btn"
            >
              <Send size={14} strokeWidth={1.5} />
              {t.contact.quickTelegram}
              <span className="transition-transform duration-300 group-hover:translate-x-1">
                →
              </span>
            </a>

            <div className="reveal reveal-delay-3 space-y-1">
              <p className="font-mono-l text-[10px] text-white/40 mb-6">
                {t.contact.direct}
              </p>

              <a
                href={`tel:${PHONE}`}
                className="group flex items-center justify-between py-5 border-t border-white/5 hover:border-[#e8d4a8]/40 transition-colors"
                data-testid="contact-phone-link"
              >
                <div className="flex items-center gap-4">
                  <Phone size={16} className="text-[#e8d4a8]" strokeWidth={1.25} />
                  <div>
                    <div className="font-mono-l text-[9px] text-white/40">
                      {t.contact.phoneLabel}
                    </div>
                    <div className="text-white text-base mt-1">{PHONE}</div>
                  </div>
                </div>
                <ArrowUpRight
                  size={16}
                  className="text-white/30 group-hover:text-[#e8d4a8] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
                />
              </a>

              <a
                href={`mailto:${EMAIL}`}
                className="group flex items-center justify-between py-5 border-t border-white/5 hover:border-[#e8d4a8]/40 transition-colors"
                data-testid="contact-email-link"
              >
                <div className="flex items-center gap-4">
                  <Mail size={16} className="text-[#e8d4a8]" strokeWidth={1.25} />
                  <div>
                    <div className="font-mono-l text-[9px] text-white/40">
                      {t.contact.emailLabel}
                    </div>
                    <div className="text-white text-base mt-1">{EMAIL}</div>
                  </div>
                </div>
                <ArrowUpRight
                  size={16}
                  className="text-white/30 group-hover:text-[#e8d4a8] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
                />
              </a>

              <a
                href={`https://t.me/${TELEGRAM_USERNAME}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-between py-5 border-t border-b border-white/5 hover:border-[#e8d4a8]/40 transition-colors"
                data-testid="contact-telegram-link"
              >
                <div className="flex items-center gap-4">
                  <Send size={16} className="text-[#e8d4a8]" strokeWidth={1.25} />
                  <div>
                    <div className="font-mono-l text-[9px] text-white/40">
                      {t.contact.telegramLabel}
                    </div>
                    <div className="text-white text-base mt-1">
                      @{TELEGRAM_USERNAME}
                    </div>
                  </div>
                </div>
                <ArrowUpRight
                  size={16}
                  className="text-white/30 group-hover:text-[#e8d4a8] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
                />
              </a>
            </div>
          </div>

          {/* Right column — Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              className="reveal reveal-delay-1 relative p-8 md:p-12 border border-white/10 bg-[#0a0a0a]/80 backdrop-blur-xl"
              data-testid="contact-form"
            >
              <div className="space-y-8">
                <div>
                  <label
                    htmlFor="name"
                    className="font-mono-l text-[10px] text-[#e8d4a8] block mb-3"
                  >
                    {t.contact.form.name}
                  </label>
                  <input
                    id="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, name: e.target.value }))
                    }
                    className="w-full bg-transparent border-b border-white/10 text-white text-lg py-3 focus:outline-none focus:border-[#e8d4a8] transition-colors"
                    data-testid="contact-input-name"
                  />
                </div>

                <div>
                  <label
                    htmlFor="contact"
                    className="font-mono-l text-[10px] text-[#e8d4a8] block mb-3"
                  >
                    {t.contact.form.contact}
                  </label>
                  <input
                    id="contact"
                    type="text"
                    required
                    value={form.contact}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, contact: e.target.value }))
                    }
                    className="w-full bg-transparent border-b border-white/10 text-white text-lg py-3 focus:outline-none focus:border-[#e8d4a8] transition-colors"
                    data-testid="contact-input-contact"
                  />
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="font-mono-l text-[10px] text-[#e8d4a8] block mb-3"
                  >
                    {t.contact.form.message}
                  </label>
                  <textarea
                    id="message"
                    rows={4}
                    required
                    value={form.message}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, message: e.target.value }))
                    }
                    className="w-full bg-transparent border-b border-white/10 text-white text-lg py-3 focus:outline-none focus:border-[#e8d4a8] transition-colors resize-none"
                    data-testid="contact-input-message"
                  />
                </div>

                <button
                  type="submit"
                  className="group inline-flex items-center gap-3 px-8 py-4 bg-[#e8d4a8] text-black text-[12px] tracking-[0.2em] uppercase font-medium hover:bg-white transition-colors duration-300"
                  data-testid="contact-submit-btn"
                >
                  <Send size={14} strokeWidth={1.5} />
                  {t.contact.form.submit}
                  <span className="transition-transform duration-300 group-hover:translate-x-1">
                    →
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
